import React, { useEffect, useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import InputField from "./InputField";
import PostWiseDetailEntry from "./PostWiseDetailEntry";
import RelevantExperienceEntry from "./RelevantExperienceEntry";
import { FaPlus } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import MultipleFileUpload from "../MultipleFileUpload";

const FieldGroup = ({
  allExpDetails,
  data,
  outerIndex,
  handleInputChange,
  addFormEntry,
  deleteFormEntry,
  addRelevantExperience,
  deleteRelevantExperience,
  fieldErrors,
  style,
  checkErrorFn,
  deleteFieldGroup,
  postandvacancyDetails,
  setFieldErrors,
  setFormData,
  formData,
  applicationDetails,
  applicationID,
  advertisementClosingDate,
  previousGroupStartDate,
  getMaxEssentialQualificationYear,
  graduationMinDate, // <-- use the prop from parent
}) => {
  const [dateError, setDateError] = useState("");

  // Validate date range and graduation min-date constraint using the prop
  const areDatesValid = () => {
    const { startDateOfService, endDateOfService } = data;

    if (!startDateOfService || !endDateOfService) {
      setDateError("");
      return true;
    }

    const start = new Date(startDateOfService);
    const end = new Date(endDateOfService);

    if (start > end) {
      setDateError("The 'To' date cannot be earlier than the 'From' date.");
      return false;
    }

    if (graduationMinDate) {
      const graduationDate = new Date(graduationMinDate);
      if (start < graduationDate) {
        const highestPassingYear = graduationDate.getFullYear();
        setDateError(
          `Experience start date cannot be earlier than your highest qualification passing year (${highestPassingYear}). Please enter a date from ${highestPassingYear}-01-01 onwards.`
        );
        return false;
      }
    }

    setDateError("");
    return true;
  };

  useEffect(() => {
    areDatesValid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.startDateOfService, data.endDateOfService, graduationMinDate]);

  const isAddPostWiseDisabled = dateError !== "";

  // Calculate experience always counting the last day (inclusive)
  // Uses month/date based calculation for accurate results
  const calculateTotalExperience = (startDate, endDate) => {
    if (!startDate || !endDate) return "";

    // Parse dates and ensure UTC to avoid timezone issues
    const start = new Date(startDate + "T00:00:00Z");
    const end = new Date(endDate + "T00:00:00Z");

    if (start > end) return "Invalid date range";

    // For inclusive calculation, we calculate the difference including both start and end dates
    // Example: Oct 1 to Oct 31 = 1 month, 0 days (spans full month)

    // Calculate years, months, days
    let years = end.getUTCFullYear() - start.getUTCFullYear();
    let months = end.getUTCMonth() - start.getUTCMonth();
    let days = end.getUTCDate() - start.getUTCDate() + 1; // +1 for inclusive (both dates count)

    // Handle negative days by borrowing from months
    if (days < 0) {
      months -= 1;
      const previousMonth = new Date(
        Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 0)
      );
      days += previousMonth.getUTCDate();
    }

    // Handle overflow: if days equals or exceeds the end month's days, convert to months
    const daysInEndMonth = new Date(
      Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 0)
    ).getUTCDate();

    // Special case: if we're in the same month and days spans the full month, it's 1 month, 0 days
    if (
      years === 0 &&
      months === 0 &&
      start.getUTCMonth() === end.getUTCMonth()
    ) {
      if (start.getUTCDate() === 1 && end.getUTCDate() === daysInEndMonth) {
        // Spans full month
        days = 0;
        months = 1;
      } else if (days >= daysInEndMonth) {
        // Days overflow, convert excess to months
        const excess = days - daysInEndMonth;
        days = excess;
        months = 1;
      }
    } else if (days > daysInEndMonth) {
      // Cross-month overflow
      const excess = days - daysInEndMonth;
      days = excess;
      months += 1;
    }

    // Handle negative months by borrowing from years
    if (months < 0) {
      years -= 1;
      months += 12;
    }

    // Handle overflow months
    if (months >= 12) {
      years += Math.floor(months / 12);
      months = months % 12;
    }

    // Calendar-wise normalization using actual month lengths (28, 29, 30, 31 days)
    // This handles leap years and different month lengths correctly
    const normalized = normalizeDaysToMonthsCalendarWise(
      years,
      months,
      days,
      end.getUTCFullYear(),
      end.getUTCMonth()
    );

    return `${normalized.years} years, ${normalized.months} months, ${normalized.days} days`;
  };

  useEffect(() => {
    const parentTotalExperience = calculateTotalExperience(
      data.startDateOfService,
      data.endDateOfService
    );
    handleInputChange(
      outerIndex,
      null,
      "totalExperience",
      parentTotalExperience
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.startDateOfService, data.endDateOfService]);

  // Update endDateOfService when status changes to "Continuing" or "Completed"
  useEffect(() => {
    if (data.statusOfService === "Continuing" && advertisementClosingDate) {
      const closingDate = advertisementClosingDate.split("T")[0];
      if (data.endDateOfService !== closingDate) {
        handleInputChange(outerIndex, null, "endDateOfService", closingDate);
      }
    } else if (data.statusOfService === "Completed") {
      const closingDate = advertisementClosingDate
        ? advertisementClosingDate.split("T")[0]
        : null;

      // Leave end date blank so applicant must pick it manually.
      if (
        !data.endDateOfService ||
        (closingDate && data.endDateOfService === closingDate)
      ) {
        handleInputChange(outerIndex, null, "endDateOfService", "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.statusOfService, advertisementClosingDate]);

  const getMinExperienceDate = () => {
    // Priority 1: use graduationMinDate from parent (which already prefers post-grad if present)
    if (graduationMinDate) return graduationMinDate;

    // Priority 2: fallback to essential qualifications max year supplied by parent helper
    const maxQualificationYear = getMaxEssentialQualificationYear();
    if (maxQualificationYear) {
      const minDate = new Date(maxQualificationYear, 0, 1);
      return minDate.toISOString().split("T")[0];
    }
    return null;
  };

  const minExperienceDate = getMinExperienceDate();

  // UTC-safe helpers to avoid timezone drift
  const toUTCDate = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
  };

  const inclusiveDaysBetweenUTC = (startYmd, endYmd) => {
    const start = toUTCDate(startYmd);
    const end = toUTCDate(endYmd);
    if (!start || !end) return 0;
    const millis = end.getTime() - start.getTime();
    return Math.floor(millis / (1000 * 60 * 60 * 24)) + 1;
  };

  // Helper function to normalize days to months using actual calendar month lengths
  // Handles 28, 29 (leap year), 30, 31 day months correctly
  const normalizeDaysToMonthsCalendarWise = (
    years,
    months,
    days,
    referenceYear,
    referenceMonth
  ) => {
    if (days === 0) return { years, months, days: 0 };

    let currentYear = referenceYear || 2000;
    let currentMonth = referenceMonth !== undefined ? referenceMonth : 0;
    let remainingDays = days;
    let additionalMonths = 0;

    // Convert excess days to months using actual calendar month lengths
    // Progressively check if we can form complete calendar months
    while (remainingDays > 0) {
      // Get days in the current month (handles leap years automatically via Date API)
      const daysInCurrentMonth = new Date(
        Date.UTC(currentYear, currentMonth + 1, 0)
      ).getUTCDate();

      if (remainingDays >= daysInCurrentMonth) {
        // Can convert a full calendar month
        remainingDays -= daysInCurrentMonth;
        additionalMonths += 1;
        currentMonth += 1;

        // Handle month overflow to next year
        if (currentMonth >= 12) {
          currentMonth = 0;
          currentYear += 1;
        }
      } else {
        // Remaining days are less than a full month, stop here
        break;
      }
    }

    // Add the additional months we calculated
    months += additionalMonths;

    // Handle months overflow to years
    if (months >= 12) {
      years += Math.floor(months / 12);
      months = months % 12;
    }

    return { years, months, days: remainingDays };
  };

  const ymdFromTotalDaysUTC = (totalDays) => {
    if (totalDays <= 0) return { years: 0, months: 0, days: 0 };

    // Use the exact same logic as calculateTotalExperience
    // Start from 1900-01-01, add totalDays-1 to get the end date
    const baseDate = new Date(Date.UTC(1900, 0, 1));
    const endDate = new Date(
      baseDate.getTime() + (totalDays - 1) * 24 * 60 * 60 * 1000
    );

    let years = endDate.getUTCFullYear() - baseDate.getUTCFullYear();
    let months = endDate.getUTCMonth() - baseDate.getUTCMonth();
    let days = endDate.getUTCDate() - baseDate.getUTCDate() + 1; // +1 for inclusive

    // Handle negative days by borrowing from months
    if (days < 0) {
      months -= 1;
      const prevMonth = new Date(
        Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), 0)
      );
      days += prevMonth.getUTCDate();
    }

    // Handle overflow: if days equals or exceeds the end month's days, convert to months
    const daysInEndMonth = new Date(
      Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth() + 1, 0)
    ).getUTCDate();

    if (
      years === 0 &&
      months === 0 &&
      baseDate.getUTCMonth() === endDate.getUTCMonth()
    ) {
      if (
        baseDate.getUTCDate() === 1 &&
        endDate.getUTCDate() === daysInEndMonth
      ) {
        // Spans full month
        days = 0;
        months = 1;
      } else if (days >= daysInEndMonth) {
        // Days overflow, convert excess to months
        const excess = days - daysInEndMonth;
        days = excess;
        months = 1;
      }
    } else if (days > daysInEndMonth) {
      // Cross-month overflow
      const excess = days - daysInEndMonth;
      days = excess;
      months += 1;
    }

    if (months < 0) {
      years -= 1;
      months += 12;
    }

    if (months >= 12) {
      years += Math.floor(months / 12);
      months = months % 12;
    }

    // Calendar-wise normalization using actual month lengths (28, 29, 30, 31 days)
    // This handles leap years and different month lengths correctly
    return normalizeDaysToMonthsCalendarWise(
      years,
      months,
      days,
      endDate.getUTCFullYear(),
      endDate.getUTCMonth()
    );
  };

  const calculateTotalExperienceSum = (entries) => {
    // Check if entries array is empty or null
    if (!entries || entries.length === 0) {
      return "0 years, 0 months, 0 days";
    }

    // If only one entry, use the individual calculation directly
    if (entries.length === 1) {
      const entry = entries[0];
      if (entry.startDateOfService && entry.endDateOfService) {
        return calculateTotalExperience(
          entry.startDateOfService,
          entry.endDateOfService
        );
      }
      return "0 years, 0 months, 0 days";
    }

    // For multiple entries, check if they are consecutive (no gaps)
    // If consecutive, use the overall date range approach
    let minStartDate = null;
    let maxEndDate = null;

    (entries || []).forEach((entry) => {
      if (entry.startDateOfService && entry.endDateOfService) {
        const start = new Date(entry.startDateOfService + "T00:00:00Z");
        const end = new Date(entry.endDateOfService + "T00:00:00Z");

        if (minStartDate === null || start < minStartDate) {
          minStartDate = start;
        }
        if (maxEndDate === null || end > maxEndDate) {
          maxEndDate = end;
        }
      }
    });

    // If no valid entries found, return 0
    if (minStartDate === null || maxEndDate === null) {
      return "0 years, 0 months, 0 days";
    }

    // Check if segments are consecutive (no gaps)
    let isConsecutive = true;
    const sortedEntries = (entries || [])
      .filter((entry) => entry.startDateOfService && entry.endDateOfService)
      .sort(
        (a, b) =>
          new Date(a.startDateOfService + "T00:00:00Z") -
          new Date(b.startDateOfService + "T00:00:00Z")
      );

    for (let i = 0; i < sortedEntries.length - 1; i++) {
      const currentEnd = new Date(
        sortedEntries[i].endDateOfService + "T00:00:00Z"
      );
      const nextStart = new Date(
        sortedEntries[i + 1].startDateOfService + "T00:00:00Z"
      );
      const nextDay = new Date(currentEnd);
      nextDay.setUTCDate(nextDay.getUTCDate() + 1);

      // Compare dates without time component
      if (
        nextDay.toISOString().split("T")[0] !==
        nextStart.toISOString().split("T")[0]
      ) {
        isConsecutive = false;
        break;
      }
    }

    // If consecutive, use the overall date range
    if (isConsecutive) {
      return calculateTotalExperience(
        minStartDate.toISOString().split("T")[0],
        maxEndDate.toISOString().split("T")[0]
      );
    }

    // If not consecutive, sum individual inclusive days and convert to years/months/days
    let totalDays = 0;

    (entries || []).forEach((entry) => {
      if (entry.startDateOfService && entry.endDateOfService) {
        const start = new Date(entry.startDateOfService + "T00:00:00Z");
        const end = new Date(entry.endDateOfService + "T00:00:00Z");
        const timeDiff = end.getTime() - start.getTime();
        const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24)) + 1; // +1 for inclusive
        totalDays += daysDiff;
      }
    });

    // If no valid entries found, return 0
    if (totalDays === 0) {
      return "0 years, 0 months, 0 days";
    }

    // Convert total days to years, months, days using month/date based calculation
    // Start from base date and add totalDays (already inclusive)
    const baseDate = new Date(Date.UTC(1900, 0, 1));
    const endDate = new Date(
      baseDate.getTime() + (totalDays - 1) * 24 * 60 * 60 * 1000
    );

    let years = endDate.getUTCFullYear() - baseDate.getUTCFullYear();
    let months = endDate.getUTCMonth() - baseDate.getUTCMonth();
    let days = endDate.getUTCDate() - baseDate.getUTCDate() + 1; // +1 for inclusive

    // Handle negative days by borrowing from months
    if (days < 0) {
      months -= 1;
      const prevMonth = new Date(
        Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), 0)
      );
      days += prevMonth.getUTCDate();
    }

    // Handle negative months by borrowing from years
    if (months < 0) {
      years -= 1;
      months += 12;
    }

    // Handle overflow months
    if (months >= 12) {
      years += Math.floor(months / 12);
      months = months % 12;
    }

    // Calendar-wise normalization using actual month lengths (28, 29, 30, 31 days)
    const normalized = normalizeDaysToMonthsCalendarWise(
      years,
      months,
      days,
      endDate.getUTCFullYear(),
      endDate.getUTCMonth()
    );

    return `${normalized.years} years, ${normalized.months} months, ${normalized.days} days`;
  };

  const calculateTotalExperienceSumWithoutTrainee = (entries) => {
    if (!entries || entries.length === 0) {
      return "0 years, 0 months, 0 days";
    }

    const nonTraineeEntries = (entries || []).filter(
      (e) => e.isTrainee !== "Yes" && e.startDateOfService && e.endDateOfService
    );

    if (nonTraineeEntries.length === 0) return "0 years, 0 months, 0 days";

    // If only one entry, use the individual calculation directly
    if (nonTraineeEntries.length === 1) {
      const entry = nonTraineeEntries[0];
      return calculateTotalExperience(
        entry.startDateOfService,
        entry.endDateOfService
      );
    }

    // For multiple entries, check if they are consecutive (no gaps)
    // If consecutive, use the overall date range approach
    let minStartDate = null;
    let maxEndDate = null;

    nonTraineeEntries.forEach((entry) => {
      const start = new Date(entry.startDateOfService + "T00:00:00Z");
      const end = new Date(entry.endDateOfService + "T00:00:00Z");

      if (minStartDate === null || start < minStartDate) {
        minStartDate = start;
      }
      if (maxEndDate === null || end > maxEndDate) {
        maxEndDate = end;
      }
    });

    // If no valid entries found, return 0
    if (minStartDate === null || maxEndDate === null) {
      return "0 years, 0 months, 0 days";
    }

    // Check if segments are consecutive (no gaps)
    let isConsecutive = true;
    const sortedEntries = nonTraineeEntries.sort(
      (a, b) =>
        new Date(a.startDateOfService + "T00:00:00Z") -
        new Date(b.startDateOfService + "T00:00:00Z")
    );

    for (let i = 0; i < sortedEntries.length - 1; i++) {
      const currentEnd = new Date(
        sortedEntries[i].endDateOfService + "T00:00:00Z"
      );
      const nextStart = new Date(
        sortedEntries[i + 1].startDateOfService + "T00:00:00Z"
      );
      const nextDay = new Date(currentEnd);
      nextDay.setUTCDate(nextDay.getUTCDate() + 1);

      // Compare dates without time component
      if (
        nextDay.toISOString().split("T")[0] !==
        nextStart.toISOString().split("T")[0]
      ) {
        isConsecutive = false;
        break;
      }
    }

    // If consecutive, use the overall date range
    if (isConsecutive) {
      return calculateTotalExperience(
        minStartDate.toISOString().split("T")[0],
        maxEndDate.toISOString().split("T")[0]
      );
    }

    // If not consecutive, sum individual inclusive days and convert to years/months/days
    let totalDays = 0;

    nonTraineeEntries.forEach((entry) => {
      if (entry.startDateOfService && entry.endDateOfService) {
        const start = new Date(entry.startDateOfService + "T00:00:00Z");
        const end = new Date(entry.endDateOfService + "T00:00:00Z");
        const timeDiff = end.getTime() - start.getTime();
        const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24)) + 1; // +1 for inclusive
        totalDays += daysDiff;
      }
    });

    // If no valid entries found, return 0
    if (totalDays === 0) {
      return "0 years, 0 months, 0 days";
    }

    // Convert total days to years, months, days using month/date based calculation
    // Start from base date and add totalDays (already inclusive)
    const baseDate = new Date(Date.UTC(1900, 0, 1));
    const endDate = new Date(
      baseDate.getTime() + (totalDays - 1) * 24 * 60 * 60 * 1000
    );

    let years = endDate.getUTCFullYear() - baseDate.getUTCFullYear();
    let months = endDate.getUTCMonth() - baseDate.getUTCMonth();
    let days = endDate.getUTCDate() - baseDate.getUTCDate() + 1; // +1 for inclusive

    // Handle negative days by borrowing from months
    if (days < 0) {
      months -= 1;
      const prevMonth = new Date(
        Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), 0)
      );
      days += prevMonth.getUTCDate();
    }

    // Handle negative months by borrowing from years
    if (months < 0) {
      years -= 1;
      months += 12;
    }

    // Handle overflow months
    if (months >= 12) {
      years += Math.floor(months / 12);
      months = months % 12;
    }

    // Calendar-wise normalization using actual month lengths (28, 29, 30, 31 days)
    const normalized = normalizeDaysToMonthsCalendarWise(
      years,
      months,
      days,
      endDate.getUTCFullYear(),
      endDate.getUTCMonth()
    );

    return `${normalized.years} years, ${normalized.months} months, ${normalized.days} days`;
  };

  const [totalExperience, setTotalExperience] = useState("");
  const [totalExperienceRelevant, setTotalExperienceRelevant] = useState("");
  const [totalExperienceWithoutTrainee, setTotalExperienceWithoutTrainee] =
    useState("");
  const [
    totalExperienceWithoutTraineeRelevant,
    setTotalExperienceWithoutTraineeRelevant,
  ] = useState("");

  useEffect(() => {
    setTotalExperience(calculateTotalExperienceSum(data.postWiseDetails));
    setTotalExperienceRelevant(
      calculateTotalExperienceSum(data.relevantExperience)
    );
    setTotalExperienceWithoutTrainee(
      calculateTotalExperienceSumWithoutTrainee(data.postWiseDetails)
    );
    setTotalExperienceWithoutTraineeRelevant(
      calculateTotalExperienceSumWithoutTrainee(data.relevantExperience)
    );
  }, [data.postWiseDetails, data.relevantExperience]);

  const experienceStringToDays = (experienceStr) => {
    if (!experienceStr || experienceStr === "Invalid date range") return 0;
    const matches = experienceStr.match(/\d+/g)?.map(Number) || [0, 0, 0];
    const [years, months, days] = matches;
    return years * 365 + months * 30 + days;
  };

  const validateExperienceConsistency = () => {
    const parentTotal = data.totalExperience;
    const sumPostWise = calculateTotalExperienceSum(data.postWiseDetails);
    const sumRelevant = calculateTotalExperienceSum(data.relevantExperience);

    const parentTotalDays = experienceStringToDays(parentTotal);
    const sumPostWiseDays = experienceStringToDays(sumPostWise);
    const sumRelevantDays = experienceStringToDays(sumRelevant);

    const isEqualExperience = (exp1, exp2) => {
      if (exp1 === "Invalid date range" || exp2 === "Invalid date range")
        return false;
      const exp1Matches = exp1.match(/\d+/g)?.map(Number) || [0, 0, 0];
      const exp2Matches = exp2.match(/\d+/g)?.map(Number) || [0, 0, 0];
      const [years1, months1, days1] = exp1Matches;
      const [years2, months2, days2] = exp2Matches;
      return years1 === years2 && months1 === months2 && days1 === days2;
    };

    if (!isEqualExperience(parentTotal, sumPostWise)) {
      setFieldErrors((prevState) => ({
        ...prevState,
        [`experienceMismatch${outerIndex}`]: {
          errors: [
            "Sum of Post Wise Experiences does not match the Total Experience.",
          ],
          touched: true,
        },
      }));
    } else {
      setFieldErrors((prevState) => {
        const { [`experienceMismatch${outerIndex}`]: _, ...rest } = prevState;
        return rest;
      });
    }

    if (sumRelevantDays > parentTotalDays) {
      setFieldErrors((prevState) => ({
        ...prevState,
        [`relevantExperienceMismatch${outerIndex}`]: {
          errors: ["Sum of Relevant Experiences exceeds the Total Experience."],
          touched: true,
        },
      }));
    } else {
      setFieldErrors((prevState) => {
        const { [`relevantExperienceMismatch${outerIndex}`]: _, ...rest } =
          prevState;
        return rest;
      });
    }
  };

  useEffect(() => {
    validateExperienceConsistency();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.totalExperience,
    data.postWiseDetails,
    data.relevantExperience,
    totalExperienceWithoutTraineeRelevant,
  ]);

  const formatIndianCurrency = (value) => {
    if (typeof value !== "string" || !value) {
      return "";
    }
    let sanitizedInput = value.replace(/[^0-9]/g, "");
    let formattedAmount = Number(sanitizedInput).toLocaleString("en-IN");
    return formattedAmount;
  };

  const isLegalDept =
    applicationDetails?.vacancyId?.department?.name &&
    applicationDetails.vacancyId.department.name.trim().toLowerCase() ===
      "legal";

  return (
    <>
      <Row>
        <Col xs={12} md={isLegalDept ? 6 : 4}>
          <InputField
            label={
              isLegalDept
                ? "Name of Organization/Court"
                : "Name of Organization"
            }
            value={data.nameOfOrganization}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(outerIndex, null, "nameOfOrganization", value);
              checkErrorFn(`nameOfOrganization${outerIndex}`, value);
            }}
            onBlur={() =>
              checkErrorFn(
                `nameOfOrganization${outerIndex}`,
                data.nameOfOrganization
              )
            }
            error={fieldErrors?.[`nameOfOrganization${outerIndex}`]?.errors}
            style={style}
            required
          />
        </Col>

        <Col xs={12} md={isLegalDept ? 6 : 4}>
          <InputField
            label="Type of Organization"
            type="select"
            value={data.typeOfOrganization}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(outerIndex, null, "typeOfOrganization", value);
              checkErrorFn(`typeOfOrganization${outerIndex}`, value);
            }}
            onBlur={() =>
              checkErrorFn(
                `typeOfOrganization${outerIndex}`,
                data.typeOfOrganization
              )
            }
            error={fieldErrors?.[`typeOfOrganization${outerIndex}`]?.errors}
            style={style}
            required
            options={[
              { value: "Private", label: "Private" },
              { value: "Government", label: "Government" },
              { value: "PSU", label: "PSU" },
              { value: "Other", label: "Other" },
            ]}
          />
        </Col>

        {!isLegalDept && (
          <Col xs={12} md={4}>
            <InputField
              label="Nature of Business of Organization"
              type="select"
              value={data?.natureOfBusiness || ""}
              onChange={(e) => {
                const value = e.target.value;
                handleInputChange(outerIndex, null, "natureOfBusiness", value);
                if (value !== "Other") {
                  handleInputChange(
                    outerIndex,
                    null,
                    "natureOfBusinessOther",
                    ""
                  );
                }
                checkErrorFn(`natureOfBusiness${outerIndex}`, value);
              }}
              onBlur={(e) => {
                const value = e.target.value || "";
                checkErrorFn(`natureOfBusiness${outerIndex}`, value);
              }}
              error={fieldErrors?.[`natureOfBusiness${outerIndex}`]?.errors}
              style={style}
              required={true}
              options={[
                ...(postandvacancyDetails?.natureOfBusiness?.map((item) => {
                  return {
                    value: `${item?.subSector}`,
                    label: `${item?.subSector}`,
                  };
                }) || []),
                { value: "Other", label: "Other" },
              ]}
            />

            {data?.natureOfBusiness === "Other" && (
              <div className="mt-2">
                <InputField
                  label="Please specify the nature of business"
                  type="text"
                  value={data?.natureOfBusinessOther || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInputChange(
                      outerIndex,
                      null,
                      "natureOfBusinessOther",
                      value
                    );
                    checkErrorFn(`natureOfBusinessOther${outerIndex}`, value);
                  }}
                  onBlur={() =>
                    checkErrorFn(
                      `natureOfBusinessOther${outerIndex}`,
                      data.natureOfBusinessOther
                    )
                  }
                  error={
                    fieldErrors?.[`natureOfBusinessOther${outerIndex}`]?.errors
                  }
                  style={style}
                  required={false}
                  placeholder="Please specify the nature of business"
                />
              </div>
            )}
          </Col>
        )}
      </Row>

      <Row className="mt-3">
        <Col xs={12} md={4}>
          <InputField
            label="Type of Service"
            type="select"
            value={data.typeOfService}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(outerIndex, null, "typeOfService", value);
              checkErrorFn(`typeOfService${outerIndex}`, value);
            }}
            onBlur={() =>
              checkErrorFn(`typeOfService${outerIndex}`, data.typeOfService)
            }
            error={fieldErrors?.[`typeOfService${outerIndex}`]?.errors}
            style={style}
            required
            options={[
              { value: "Regular", label: "Regular" },
              { value: "Contractual", label: "Contractual" },
              ...(applicationDetails?.vacancyId?.department?.name &&
              applicationDetails.vacancyId.department.name
                .trim()
                .toLowerCase() === "legal"
                ? [{ value: "Practice", label: "Practice" }]
                : []),
            ]}
          />
        </Col>

        <Col xs={12} md={4}>
          <InputField
            label="Status of Service"
            type="select"
            value={data.statusOfService}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(outerIndex, null, "statusOfService", value);
              checkErrorFn(`statusOfService${outerIndex}`, value);
            }}
            onBlur={() =>
              checkErrorFn(`statusOfService${outerIndex}`, data.statusOfService)
            }
            error={fieldErrors?.[`statusOfService${outerIndex}`]?.errors}
            style={style}
            required
            options={[
              { value: "Completed", label: "Separated" },
              { value: "Continuing", label: "Continuing" },
            ]}
          />
        </Col>

        <Col xs={12} md={4}>
          <InputField
            label={
              isLegalDept
                ? "Posting / Practice Location(s)"
                : "Posting Location(s)"
            }
            type="text"
            value={data?.postingLocation}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(outerIndex, null, "postingLocation", value);
              checkErrorFn(`postingLocation${outerIndex}`, value);
            }}
            onBlur={() =>
              checkErrorFn(`postingLocation${outerIndex}`, data.postingLocation)
            }
            error={fieldErrors?.[`postingLocation${outerIndex}`]?.errors}
            style={style}
            required
          />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col xs={12} md={4}>
          <InputField
            label="From"
            type="date"
            value={data.startDateOfService}
            onChange={(e) => {
              let enteredDate = e.target.value;
              
              // Validate against minimum date (highest qualification passing year)
              if (enteredDate && minExperienceDate) {
                const entered = new Date(enteredDate);
                const minDate = new Date(minExperienceDate);
                
                // If entered date is before minimum date, set to minimum date
                if (entered < minDate) {
                  enteredDate = minExperienceDate;
                }
              }
              
              handleInputChange(
                outerIndex,
                null,
                "startDateOfService",
                enteredDate
              );
              
              // Validate immediately after setting
              if (enteredDate) {
                checkErrorFn(
                  `startDateOfService${outerIndex}`,
                  enteredDate
                );
              }
            }}
            onBlur={() => {
              let correctedDate = data.startDateOfService;
              
              // Validate and correct on blur if date is invalid (against highest qualification passing year)
              if (correctedDate && minExperienceDate) {
                const entered = new Date(correctedDate);
                const minDate = new Date(minExperienceDate);
                
                // If entered date is before minimum date, correct to minimum date
                if (entered < minDate) {
                  correctedDate = minExperienceDate;
                  handleInputChange(
                    outerIndex,
                    null,
                    "startDateOfService",
                    correctedDate
                  );
                }
              }
              
              checkErrorFn(
                `startDateOfService${outerIndex}`,
                correctedDate
              );
            }}
            min={minExperienceDate}
            max={
              previousGroupStartDate
                ? new Date(
                    new Date(previousGroupStartDate).getTime() - 86400000
                  )
                    .toISOString()
                    .split("T")[0]
                : new Date().toISOString().split("T")[0]
            }
            error={fieldErrors?.[`startDateOfService${outerIndex}`]?.errors}
            style={style}
            required
          />
        </Col>

        <Col xs={12} md={4}>
          <InputField
            label="To"
            type="date"
            value={
              data.statusOfService === "Continuing" && advertisementClosingDate
                ? advertisementClosingDate.split("T")[0]
                : data.endDateOfService
            }
            onChange={(e) => {
              let enteredDate = e.target.value;
              
              // Validate against minimum date (highest qualification passing year)
              if (enteredDate && minExperienceDate) {
                const entered = new Date(enteredDate);
                const minDate = new Date(minExperienceDate);
                
                // If entered date is before minimum date, set to minimum date
                if (entered < minDate) {
                  enteredDate = minExperienceDate;
                }
              }
              
              // Also ensure "To" date is not before "From" date
              if (enteredDate && data.startDateOfService) {
                const entered = new Date(enteredDate);
                const fromDate = new Date(data.startDateOfService);
                
                if (entered < fromDate) {
                  enteredDate = data.startDateOfService;
                }
              }
              
              handleInputChange(
                outerIndex,
                null,
                "endDateOfService",
                enteredDate
              );
              
              // Validate immediately after setting
              if (enteredDate) {
                checkErrorFn(
                  `endDateOfService${outerIndex}`,
                  enteredDate
                );
              }
            }}
            onBlur={() => {
              let correctedDate = data.endDateOfService;
              
              // Validate and correct on blur if date is invalid
              if (correctedDate && minExperienceDate) {
                const entered = new Date(correctedDate);
                const minDate = new Date(minExperienceDate);
                
                // If entered date is before minimum date, correct to minimum date
                if (entered < minDate) {
                  correctedDate = minExperienceDate;
                  handleInputChange(
                    outerIndex,
                    null,
                    "endDateOfService",
                    correctedDate
                  );
                }
              }
              
              // Also ensure "To" date is not before "From" date
              if (correctedDate && data.startDateOfService) {
                const entered = new Date(correctedDate);
                const fromDate = new Date(data.startDateOfService);
                
                if (entered < fromDate) {
                  correctedDate = data.startDateOfService;
                  handleInputChange(
                    outerIndex,
                    null,
                    "endDateOfService",
                    correctedDate
                  );
                }
              }
              
              checkErrorFn(
                `endDateOfService${outerIndex}`,
                correctedDate
              );
            }}
            min={minExperienceDate}
            max={
              previousGroupStartDate
                ? new Date(
                    new Date(previousGroupStartDate).getTime() - 86400000
                  )
                    .toISOString()
                    .split("T")[0]
                : data.statusOfService === "Continuing"
                ? advertisementClosingDate
                  ? advertisementClosingDate.split("T")[0]
                  : ""
                : new Date().toLocaleDateString("en-CA")
            }
            error={fieldErrors?.[`endDateOfService${outerIndex}`]?.errors}
            style={style}
            required
            readOnly={data.statusOfService === "Continuing"}
          />
        </Col>

        <Col xs={12} md={4}>
          <InputField
            label="Total Experience"
            value={data.totalExperience}
            onChange={() => {}}
            style={style}
            required
            readOnly={true}
          />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col xs={12} md={4}>
          <InputField
            label="Pay Scale (₹)"
            value={formatIndianCurrency(data.payScale)}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(outerIndex, null, "payScale", value);
            }}
            style={style}
          />
        </Col>

        <Col xs={12} md={4}>
          <InputField
            label="Basic Pay (₹)"
            value={formatIndianCurrency(data.basicPay)}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(outerIndex, null, "basicPay", value);
              checkErrorFn(`basicPay${outerIndex}`, value);
            }}
            onBlur={() => checkErrorFn(`basicPay${outerIndex}`, data.basicPay)}
            error={fieldErrors?.[`basicPay${outerIndex}`]?.errors}
            style={style}
            required={
              applicationDetails?.vacancyId?.department?.name !== "Legal"
            }
          />
        </Col>

        <Col xs={12} md={4}>
          <InputField
            label={
              applicationDetails?.vacancyId?.department?.name === "Legal"
                ? "Annual Income / CTC (₹)"
                : "CTC (₹)"
            }
            value={formatIndianCurrency(data.ctc)}
            onChange={(e) => {
              const value = e.target.value;
              handleInputChange(outerIndex, null, "ctc", value);
              checkErrorFn(`ctc${outerIndex}`, value);
            }}
            onBlur={() => checkErrorFn(`ctc${outerIndex}`, data.ctc)}
            error={fieldErrors?.[`ctc${outerIndex}`]?.errors}
            style={style}
            required
          />
        </Col>
      </Row>

      <Row className="mt-4">
        <Col xs={12}>
          <label
            className="experience-section-heading mt-4 mb-1 ps-0 ms-0 pb-1"
            style={style.label}
          >
            <h6 className="experience-section-heading ms-0 ps-0 pb-0">
              Post Wise Details:
            </h6>
          </label>

          {data.postWiseDetails?.map((entry, innerIndex) => (
            <PostWiseDetailEntry
              calculateTotalExperience={calculateTotalExperience}
              key={innerIndex}
              entry={entry}
              outerIndex={outerIndex}
              innerIndex={innerIndex}
              handleInputChange={handleInputChange}
              deleteFormEntry={deleteFormEntry}
              fieldErrors={fieldErrors}
              style={style}
              checkErrorFn={checkErrorFn}
              setFieldErrors={setFieldErrors}
              parentStartDate={data.startDateOfService}
              parentEndDate={data.endDateOfService}
              validateExperienceConsistency={validateExperienceConsistency}
              postWiseTotalWithoutTraineeFun={() => {
                setTotalExperience(
                  calculateTotalExperienceSum(data.postWiseDetails)
                );
                setTotalExperienceWithoutTrainee(
                  calculateTotalExperienceSumWithoutTrainee(
                    data.postWiseDetails
                  )
                );
              }}
              allEntries={data.postWiseDetails}
              minExperienceDate={minExperienceDate}
              applicationDetails={applicationDetails}
            />
          ))}

          <Row className="mt-2">
            <Col xs={12} md={4} className="mt-2 mt-md-0">
              <label className="form-label" style={style.label}>
                Total Post Wise Experience:
                <br />
                <span style={{ color: "#921D1E" }}>{totalExperience}</span>
              </label>
            </Col>
            <Col xs={12} md={4} className="mt-2 mt-md-0">
              <label className="form-label" style={style.label}>
                Total Post Wise Experience (Excluding Trainees):
                <br />
                <span style={{ color: "#921D1E" }}>
                  {totalExperienceWithoutTrainee}
                </span>
              </label>
            </Col>
            <Col
              xs={12}
              md={4}
              className="d-flex justify-content-start justify-content-md-end mt-2 mt-md-0"
            >
              <Button
                variant="primary"
                className="mt-2"
                onClick={() => addFormEntry(outerIndex)}
                disabled={isAddPostWiseDisabled}
                title={
                  isAddPostWiseDisabled
                    ? "Please enter both From and To dates to add a post-wise detail."
                    : "Add Post Wise Detail"
                }
              >
                <FaPlus /> Add Post Wise Detail
              </Button>
            </Col>
          </Row>

          {fieldErrors?.[`experienceMismatch${outerIndex}`]?.errors && (
            <div className="text-danger mt-2">
              {fieldErrors[`experienceMismatch${outerIndex}`]?.errors[0]}
            </div>
          )}

          <hr />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col xs={12} md={12} className="d-flex align-items-start mb-3">
          {postandvacancyDetails?.relevantExperiences?.[0]?.experience && (
            <>
              <input
                className="mt-2"
                type="checkbox"
                checked={data.isRelevantChecked || false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  handleInputChange(
                    outerIndex,
                    null,
                    "isRelevantChecked",
                    isChecked
                  );
                  
                  // When unchecked, clear relevant experience data to update global totals
                  if (!isChecked) {
                    handleInputChange(
                      outerIndex,
                      null,
                      "relevantExperience",
                      []
                    );
                  }
                }}
                id={`isRelevant${outerIndex}`}
              />
              <label
                htmlFor={`isRelevant${outerIndex}`}
                className="form-label ms-2 mt-0"
                style={style.label}
              >
                Relevant experience : "
                {postandvacancyDetails?.relevantExperiences?.[0]?.experience}"
                <span className="mx-1 text-danger">*</span>
              </label>
            </>
          )}
        </Col>
      </Row>

      {data.isRelevantChecked &&
        postandvacancyDetails?.relevantExperiences?.[0]?.experience && (
          <Row className="mt-2">
            <Col xs={12}>
              <label className="form-label" style={style.label}>
                Relevant Experience:
              </label>

              {data.relevantExperience?.map((entry, relevantIndex) => (
                <RelevantExperienceEntry
                  calculateTotalExperience={calculateTotalExperience}
                  key={relevantIndex}
                  entry={entry}
                  outerIndex={outerIndex}
                  relevantIndex={relevantIndex}
                  handleInputChange={handleInputChange}
                  deleteRelevantExperience={deleteRelevantExperience}
                  fieldErrors={fieldErrors}
                  style={style}
                  checkErrorFn={checkErrorFn}
                  setFieldErrors={setFieldErrors}
                  parentStartDate={data.startDateOfService}
                  parentEndDate={data.endDateOfService}
                  validateExperienceConsistency={validateExperienceConsistency}
                  relaventTotalWithoutTraineeFun={() => {
                    setTotalExperienceRelevant(
                      calculateTotalExperienceSum(data.relevantExperience)
                    );
                    setTotalExperienceWithoutTraineeRelevant(
                      calculateTotalExperienceSumWithoutTrainee(
                        data.relevantExperience
                      )
                    );
                  }}
                  allEntries={data.relevantExperience}
                  innerIndex={relevantIndex}
                  minExperienceDate={minExperienceDate}
                  applicationDetails={applicationDetails}
                />
              ))}

              <Row className="mt-4">
                <Col xs={12} md={4}>
                  <label className="form-label" style={style.label}>
                    Total Relevant Experience:
                    <br />
                    <span style={{ color: "#921D1E" }}>
                      {totalExperienceRelevant}
                    </span>
                  </label>
                </Col>
                <Col xs={12} md={4} className="mt-2 mt-md-0">
                  <label className="form-label" style={style.label}>
                    Total Relevant Experience (Excluding Trainees):
                    <br />
                    <span style={{ color: "#921D1E" }}>
                      {totalExperienceWithoutTraineeRelevant}
                    </span>
                  </label>
                </Col>
                <Col
                  xs={12}
                  md={4}
                  className="mt-2 mt-md-0 d-flex justify-content-start justify-content-md-end "
                >
                  <Button
                    variant="primary"
                    onClick={() => addRelevantExperience(outerIndex)}
                  >
                    <FaPlus /> Add Relevant Experience for The Post
                  </Button>
                </Col>
              </Row>

              {fieldErrors?.[`relevantExperienceMismatch${outerIndex}`]
                ?.errors && (
                <div className="text-danger mt-2">
                  {
                    fieldErrors[`relevantExperienceMismatch${outerIndex}`]
                      ?.errors[0]
                  }
                </div>
              )}

              <hr />
            </Col>
          </Row>
        )}

      <Row className="mt-4">
        <Col xs={12}>
          <MultipleFileUpload
            data={data}
            outerIndex={outerIndex}
            handleInputChange={handleInputChange}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
            style={style}
            checkErrorFn={checkErrorFn}
            applicationDetails={applicationDetails}
            applicationID={applicationID}
          />
        </Col>
      </Row>

      <Row className="ms-0 ms-md-1 text-start text-md-center mt-2 mb-4">
        <Col xs={12} className="ps-0">
          <Button
            variant="danger"
            onClick={() => deleteFieldGroup(outerIndex)}
            className="ms-0"
          >
            <RiDeleteBin6Line /> Delete Experience
          </Button>
        </Col>
      </Row>
      <hr />
    </>
  );
};

export default React.memo(FieldGroup);
